import React from "react";
import LegalLayout, { LegalSection, LegalList } from "@/components/legal/LegalLayout";
import { LifeBuoy, Clock, Bug, Lightbulb, MessageSquare } from "lucide-react";

/**
 * Support page — public, no auth.
 *
 * Centralised destination linked from the footer, user menu, and help menu.
 * Provides email contact, response-time expectations, and structured guidance
 * for reporting issues / submitting feedback / requesting features.
 */
export default function Support() {
  return (
    <LegalLayout
      testid="page-support"
      eyebrow="Trust · Support"
      title="ForgedOps Plans Support"
      intro="Need help with ForgedOps Plans? Reach the operator team directly. We respond during normal U.S. Eastern business hours and prioritize production-impacting issues. Looking for a walkthrough of the core workflows instead? Visit Help."
      metaTitle="Support · ForgedOps Plans"
      metaDescription="ForgedOps Plans support — email support@forgedopshq.com for help, bug reports, and feature requests. Response within 1 business day for production issues."
      canonical="https://forgedopsplans.com/support"
    >
      <section className="not-prose" data-testid="support-cards">
        <div className="grid sm:grid-cols-2 gap-4 mb-2">
          <SupportCard
            icon={LifeBuoy}
            label="Primary contact"
            primary={
              <a
                href="mailto:support@forgedopshq.com"
                className="underline underline-offset-2"
                style={{ color: "var(--fop-primary)" }}
                data-testid="support-primary-email"
              >
                support@forgedopshq.com
              </a>
            }
            secondary="For help, account issues, billing, and anything platform-related."
          />
          <SupportCard
            icon={MessageSquare}
            label="Alternate contact"
            primary={
              <a
                href="mailto:info@forgedopshq.com"
                className="underline underline-offset-2"
                style={{ color: "var(--fop-primary)" }}
                data-testid="support-alt-email"
              >
                info@forgedopshq.com
              </a>
            }
            secondary="Use this address if support@ is unreachable, or for general inquiries."
          />
        </div>
      </section>

      <LegalSection id="response" title="Response Expectations">
        <p>We aim to respond to every email within one (1) U.S. business day. Production-impacting issues are triaged immediately when received during business hours.</p>
        <LegalList items={[
          "Production outage / data unavailable — same-day response, active triage.",
          "Functional bug / blocked workflow — next business day.",
          "Feature request / general question — within three (3) business days.",
        ]} />
        <div
          className="mt-3 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-mono uppercase tracking-[0.16em]"
          style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface)", color: "var(--fop-text-2)" }}
        >
          <Clock size={14} style={{ color: "var(--fop-primary)" }} />
          Mon – Fri · 8a – 6p ET
        </div>
      </LegalSection>

      <LegalSection id="report-issue" title="Reporting an Issue">
        <p>To help us reproduce and resolve quickly, please include the following when emailing support:</p>
        <LegalList items={[
          "Your tenant slug or company name.",
          "The page URL or screen where the issue occurred.",
          "What you expected to happen.",
          "What actually happened (a screenshot is ideal).",
          "Timestamp (your local time and timezone).",
          "Browser + OS (e.g. Chrome 121 on macOS 14).",
        ]} />
        <p>If the issue affects production work or field operations, write &ldquo;<strong>PRODUCTION</strong>&rdquo; in the subject line and we will route immediately.</p>
      </LegalSection>

      <LegalSection id="feedback" title="Submit Feedback">
        <p>We read every piece of feedback. Tell us what is working, what is not, and what you wish the platform could do. Email <a href="mailto:support@forgedopshq.com" className="underline" style={{ color: "var(--fop-primary)" }}>support@forgedopshq.com</a> with the subject line &ldquo;Feedback&rdquo;.</p>
        <p>You can also use the in-app <span className="inline-flex items-center gap-1 font-mono text-[12px] uppercase tracking-[0.15em]"><MessageSquare size={12} style={{ color: "var(--fop-primary)" }} /> Feedback</span> trigger in the sidebar when signed in.</p>
      </LegalSection>

      <LegalSection id="report-bug" title="Report a Bug">
        <p>Email <a href="mailto:support@forgedopshq.com" className="underline" style={{ color: "var(--fop-primary)" }}>support@forgedopshq.com</a> with the subject line &ldquo;Bug Report&rdquo;. Attach a screenshot and include the reproduction steps from the section above.</p>
        <div className="flex items-start gap-2 mt-3">
          <Bug size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <span className="text-sm" style={{ color: "var(--fop-text-2)" }}>
            For security-sensitive vulnerabilities, please use <a href="mailto:info@forgedopshq.com" className="underline" style={{ color: "var(--fop-primary)" }}>info@forgedopshq.com</a> and mark the subject line &ldquo;Security&rdquo;.
          </span>
        </div>
      </LegalSection>

      <LegalSection id="feature" title="Request a Feature">
        <p>Have an idea that would change your operations? Email <a href="mailto:support@forgedopshq.com" className="underline" style={{ color: "var(--fop-primary)" }}>support@forgedopshq.com</a> with the subject line &ldquo;Feature Request&rdquo;. Tell us the workflow you are trying to improve, who would use it, and how often.</p>
        <div className="flex items-start gap-2 mt-3">
          <Lightbulb size={16} style={{ color: "var(--fop-primary)" }} className="mt-0.5" />
          <span className="text-sm" style={{ color: "var(--fop-text-2)" }}>
            We prioritize feature requests based on field-operations impact and how many customers will benefit.
          </span>
        </div>
      </LegalSection>

      <LegalSection id="status" title="Service Status">
        <p>The platform exposes anonymous health endpoints you can probe at any time:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><code className="font-mono text-[13px]">https://forgedopsplans.com/api/health</code> — application + database + storage + email configuration check.</li>
          <li><code className="font-mono text-[13px]">https://forgedopsplans.com/api/system_health</code> — same check, alternate path for monitoring tools.</li>
        </ul>
        <p>For broader incident updates, contact <a href="mailto:support@forgedopshq.com" className="underline" style={{ color: "var(--fop-primary)" }}>support@forgedopshq.com</a>.</p>
      </LegalSection>
    </LegalLayout>
  );
}

function SupportCard({ icon: Icon, label, primary, secondary }) {
  return (
    <div
      className="rounded-md border p-5"
      style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: "var(--fop-primary)" }} />
        <div
          className="font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: "var(--fop-text-3)" }}
        >
          {label}
        </div>
      </div>
      <div className="text-lg font-display mb-2" style={{ color: "var(--fop-text)" }}>
        {primary}
      </div>
      {secondary && (
        <div className="text-sm leading-relaxed" style={{ color: "var(--fop-text-2)" }}>
          {secondary}
        </div>
      )}
    </div>
  );
}
